import axios from "axios";
import toast from 'react-hot-toast';
import { productMappings } from "../../constants/productMappings";
import { banks } from "../../constants/banks";
import { formatPhoneNumber } from 'react-phone-number-input';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer';
import ProposalPDF from '../../pages/ProposalPDF';
import { DEPENDENT_CVT_CODES, MAX_CHILDREN } from '../../constants/dependentMapping';
import { store } from '../../store/store';
import { handleSessionExpired } from '../sessionExpiry';


const apiUrl = `${process.env.REACT_APP_API_URL}`;
const apiBaseUrl = `${process.env.REACT_APP_API_BASE_URL}`;
const apiKey = process.env.REACT_APP_API_KEY;
const apiUser = process.env.REACT_APP_API_USER;
const liveVerificationApiUrl = 'https://slac-api.starlifecentral.com/StarLife';

const getStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const getAccessToken = () => (
  store.getState().auth?.accessToken
  || getStoredJson('agentData')?.access_token
  || null
);



export const restructureOngoingApplications = () => {
  let ongoingApplications = JSON.parse(localStorage.getItem('ongoingApplications')) || [];

  // Check if the first element is an array, indicating an array of arrays structure
  if (ongoingApplications.length > 0 && Array.isArray(ongoingApplications[0])) {
    // Flatten the array of arrays to a single-level array of objects
    ongoingApplications = ongoingApplications.flat();
    localStorage.setItem('ongoingApplications', JSON.stringify(ongoingApplications));
    console.log('Ongoing applications have been successfully restructured.');
  } else {
    console.log('Ongoing applications are already in the correct format.');
  }
};



export const determineStepsCount = (productInitials) => {
  const stepsMapping = {
    'WPP': 7,
    'CLP': 7,
    'UPP': 7,
    'EHP': 8,
    'CBP': 7,
    'FPP': 8,
    'FCL': 7,
    'FWP': 7,
    'FUP': 7,
    'FEH': 8,
    'PCL': 7,
    'PUP': 7,
    'PWP': 7,
    'PEH': 8,
  };
  return stepsMapping[productInitials] || 0;
};



export const submitTelesalesProposal = async (user) => {
  const app = JSON.parse(localStorage.getItem('ongoingApplications') || '[]')
    .find(a => a.id === localStorage.getItem('currentApplicationID'));
  
  if (!app) return { success: false, error: 'No application' };
  
  const client = app.data?.clientData || {};
  
  const payload = {
    agent_code: user.agent_code,
    client_code: client.New_Client_Code || client.existingClientCode,
    quote_code: app.data?.productDetailsData?.quoteDetails?.quote_code || '', // Direct from app structure
    client_name: `${client.firstName} ${client.otherNames} ${client.lastName}`.trim(),
    client_phone: client.mobileNumber || '',
    product_name: app.product || '',
    client_consent: client.consentToUseSignature || 'NO',
    full_data: app
  };
  
  try {
    const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/agent/submit-client-proposal`, payload);
    const success = res.data?.message?.toLowerCase().includes('success');
    return { success, response: res.data, error: success ? undefined : res.data?.message };
  } catch (e) {
    return { success: false, error: e.response?.data?.message || e.message };
  }
};



export const updateFormSubmissionStatus = (applicationId, stepIndex, success) => {
  //console.log('updateFormSubmissionStatus', applicationId, stepIndex, success);
  let ongoingApplications = JSON.parse(localStorage.getItem('ongoingApplications')) || [];
  
  // Update to iterate over each group and then each application within that group
  ongoingApplications = ongoingApplications.map(group => 
    group.map(application => {
      if (application.id === applicationId) {
        // Ensure formSubmissionStatus object exists
        if (!application.formSubmissionStatus) {
          application.formSubmissionStatus = {};
        }
        // Update the specific step status
        application.formSubmissionStatus[stepIndex] = success;
      }
      return application;
    })
  );
  
  localStorage.setItem('ongoingApplications', JSON.stringify(ongoingApplications));
};


export const updateOngoingApplicationData = (applicationId, dataUpdater) => {
  const ongoingApplications = JSON.parse(localStorage.getItem('ongoingApplications') || '[]');

  if (!Array.isArray(ongoingApplications) || !applicationId) {
    return null;
  }

  let updatedApplication = null;
  const updatedApplications = ongoingApplications.map((application) => {
    if (application.id !== applicationId) {
      return application;
    }

    const nextData = typeof dataUpdater === 'function'
      ? dataUpdater(application.data || {}, application)
      : { ...(application.data || {}), ...(dataUpdater || {}) };

    updatedApplication = {
      ...application,
      data: nextData,
    };

    return updatedApplication;
  });

  localStorage.setItem('ongoingApplications', JSON.stringify(updatedApplications));
  return updatedApplication;
};






export function generateApplicationID(productName) {
  if (!productName || typeof productName !== 'string') {
    console.error('Invalid productName:', productName);
    return ''; // Return an empty string or some default ID
  }

  let initials;
  const match = productName.match(/\(([^)]+)\)/);

  if (match) {
    initials = match[1];
  } else {
    const mapping = productMappings.find(entry => entry.productName === productName);
    initials = mapping ? mapping.initials : '';
  }

  const timestamp = Date.now();
  return initials + timestamp;
}


export function getProductInitials(product) {
  const productName = product?.name || product || '';
  if (!productName || typeof productName !== 'string') {
    return '';
  }

  const match = productName.match(/\(([^)]+)\)/);
  if (match) {
    return match[1].trim().toUpperCase();
  }

  const mapping = productMappings.find((entry) => entry.productName === productName);
  return (mapping?.initials || '').toUpperCase();
}


export async function fetchAuthenticatedExistingPolicies(phoneNumber, options = {}) {
  if (!phoneNumber) {
    return null;
  }

  const token = options.accessToken || getAccessToken();
  if (!token) {
    throw new Error('Authentication token is missing. Please sign in again.');
  }

  const normalizedPhoneNumber = phoneNumber.startsWith('+233')
    ? formatPhoneNumber(phoneNumber).replace(/\s/g, '')
    : phoneNumber.replace(/\s/g, '');

  try {
    const payload = {
      phone_number: normalizedPhoneNumber,
    };

    if (options.productInitials) {
      payload.product_initials = options.productInitials;
    }

    const response = await axios.post(
      `${apiBaseUrl}/onboarding/policies/check-existing`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      handleSessionExpired();
      return null;
    }

    throw new Error(
      error.response?.data?.message || 'Unable to verify policy purchase eligibility right now.'
    );
  }
}


export async function fetchPolicyPurchaseEligibility(phoneNumber, productInitials, options = {}) {
  if (!productInitials) {
    return null;
  }

  return fetchAuthenticatedExistingPolicies(phoneNumber, {
    ...options,
    productInitials,
  });
}


export function getPolicyPurchaseBlockMessage(eligibility, productInitials) {
  const target = eligibility?.policy_limit_summary?.target;
  if (!target || target.product_initials !== productInitials) {
    return null;
  }

  if (target.blocking_reason === 'matching_policy_exists') {
    return 'This client already has an active or draft policy for this product.';
  }

  if (target.blocking_reason === 'family_limit_reached') {
    const family = eligibility?.policy_limit_summary?.families?.[target.product_family];
    const heldInitials = family?.held_initials?.length ? family.held_initials.join(', ') : productInitials;
    return `This client has already reached the maximum of ${target.max_allowed} ${target.display_name.toLowerCase()} allowed. Current holdings: ${heldInitials}.`;
  }

  return null;
}


export const updateCurrentApplicationID = (newApplicationID) => {
  localStorage.removeItem('currentApplicationID');
  
  localStorage.setItem('currentApplicationID', newApplicationID);
}



export const verifyMomoNumber = async (phoneNumber, networkName) => {
  try {
    const payload = {
      Phone_Number: phoneNumber.replace(/^\+233/, '0').replace(/\s/g, ''),
      Network_Name: networkName,
      Api_User: apiUser,
      Api_Key: apiKey,
    };
    const response = await axios.post(`${liveVerificationApiUrl}/SL_VerifyMomoNumber`, payload);

    const accountName = response?.data?.data?.account_name;
    return typeof accountName === 'string' ? accountName.trim() : '';
  } catch (error) {
    console.error(error);
    return '';
  }
};



export const verifyBankAccountNumber = async (
  accountNumber,
  bankName,
  setBankNameVerification = () => {}
) => {
  try {
    const payload = {
      Account_Number: accountNumber,
      Bank_Name: bankName,
      Api_User: apiUser,
      Api_Key: apiKey,
    };

    const response = await axios.post(`${liveVerificationApiUrl}/SL_VerifyBankAccountNumber`, payload);

    const accountName = typeof response?.data?.data?.account_name === 'string'
      ? response.data.data.account_name.trim()
      : '';

    setBankNameVerification(accountName);

    return accountName;
  } catch (error) {
    console.error(error);
    setBankNameVerification('');
    return '';
  }
};



export const fetchBanks = async (setBanks) => {
  try {
    const payload = {
      Api_User: apiUser,
      Api_Key: apiKey,
    };

    const response = await axios.post(`${apiUrl}/SL_Banks`, payload);

    const fetchedBanks = response.data;

    if (fetchedBanks && fetchedBanks.length > 0) {
      setBanks(fetchedBanks);
    } else {
      setBanks(banks);
    }
  } catch (error) {
    console.error('Error fetching banks:', error);
    setBanks(banks);
  }
};




export const fetchBankBranches = async (bnkCode, setBankBranches) => {
  try {
    const payload = {
      Api_User: apiUser,
      Api_Key: apiKey,
      Bank_Code: bnkCode.toString(),
    };
    const response = await axios.post(`${apiUrl}/SL_BankBranches`, payload);

    const fetchedBranches = response.data;
    const branchNames = fetchedBranches.map((branch) => branch.bbrBranchName);
    setBankBranches(branchNames);
  } catch (error) {
    console.error(error);
  }
};


export const benefitTypeToPolicyShortCodes = {
  MATURITY: ['CLP', 'SCB', 'SCL', 'CBP', 'WPP'],
  LOAN: ['SCB', 'CLP', 'WPP'],
  "PARTIAL WITHDRAWAL": ['SCB', 'SCL', 'CBP'],
  CASHBACK: ['HPP', 'SHPP', 'EHC', 'EHP', 'FPP', 'GHC'],
  SURRENDER: ['SCB', 'CLP', 'CBP', 'WPP', 'SCL']
};

export const normalizeBenefitRequestType = (benefitType = '') => {
  const normalized = String(benefitType || '').trim().toUpperCase();

  if (normalized === 'MATURITY CLAIM') {
    return 'MATURITY';
  }

  if (normalized === 'POLICY REFUNDS' || normalized === 'NON-POLICY REFUNDS') {
    return 'REFUND';
  }

  return normalized;
};

export const getBenefitRequestServiceConfig = (benefitType = '') => {
  const normalizedType = normalizeBenefitRequestType(benefitType);

  switch (normalizedType) {
    case 'CASHBACK':
      return {
        normalizedType,
        amountEndpoint: 'SL_CashBack',
        transactionType: 'CASHBACK REQUEST',
        serviceTypeCode: 8,
        serviceCategoryCode: 11,
      };
    case 'PARTIAL WITHDRAWAL':
      return {
        normalizedType,
        amountEndpoint: 'SL_WithdrawableAmount',
        transactionType: 'PARTIAL WITHDRAWAL REQUEST',
        serviceTypeCode: 8,
        serviceCategoryCode: 10,
      };
    case 'MATURITY':
      return {
        normalizedType,
        amountEndpoint: null,
        transactionType: 'MATURITY REQUEST',
        serviceTypeCode: 8,
        serviceCategoryCode: 13,
      };
    case 'REFUND':
      return {
        normalizedType,
        amountEndpoint: null,
        transactionType: 'REFUND REQUEST',
        serviceTypeCode: 8,
        serviceCategoryCode: 12,
      };
    case 'SURRENDER':
      return {
        normalizedType,
        amountEndpoint: null,
        transactionType: 'SURRENDER REQUEST',
        serviceTypeCode: 8,
        serviceCategoryCode: 14,
      };
    default:
      return {
        normalizedType,
        amountEndpoint: null,
        transactionType: `${normalizedType || 'BENEFIT'} REQUEST`,
        serviceTypeCode: 8,
        serviceCategoryCode: 14,
      };
  }
};

export const formatBenefitPayModeForApi = (paymentMode = '') => {
  if (paymentMode === 'Bank') {
    return 'Bank Transfer';
  }

  if (paymentMode === 'Mobile Money') {
    return 'MOMO';
  }

  return paymentMode || '';
};

export const extractPolicySumAssured = (policyDetails = {}) => {
  return toFiniteAmount(policyDetails?.endrTotalSa);
};

export const extractPolicyRefundAmount = (policyDetails = {}) => {
  return (
    toFiniteAmount(policyDetails?.grossPremium) ??
    toFiniteAmount(policyDetails?.totPremiumPaid) ??
    toFiniteAmount(policyDetails?.polTotalContribution) ??
    toFiniteAmount(policyDetails?.refundAmount) ??
    toFiniteAmount(policyDetails?.RefundAmount) ??
    null
  );
};

const BENEFIT_AMOUNT_CANDIDATE_KEYS = [
  'amount',
  'Amount',
  'cashBackAmount',
  'CashBackAmount',
  'cashbackAmount',
  'withdrawableAmount',
  'WithdrawableAmount',
  'withdrawableAmt',
  'WithdrawableAmt',
  'maturityAmount',
  'MaturityAmount',
  'refundAmount',
  'RefundAmount',
  'availableAmount',
  'AvailableAmount',
];

const toFiniteAmount = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const numericValue = Number(value.replace(/[^\d.-]/g, ''));
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return null;
};

export const extractBenefitAmount = (input) => {
  if (input === null || input === undefined) {
    return null;
  }

  const primitiveAmount = toFiniteAmount(input);
  if (primitiveAmount !== null) {
    return primitiveAmount;
  }

  if (Array.isArray(input)) {
    for (const item of input) {
      const amount = extractBenefitAmount(item);
      if (amount !== null) {
        return amount;
      }
    }
    return null;
  }

  if (typeof input !== 'object') {
    return null;
  }

  for (const key of BENEFIT_AMOUNT_CANDIDATE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      const amount = extractBenefitAmount(input[key]);
      if (amount !== null) {
        return amount;
      }
    }
  }

  for (const value of Object.values(input)) {
    const amount = extractBenefitAmount(value);
    if (amount !== null) {
      return amount;
    }
  }

  return null;
};

export const extractBenefitAmountResponse = (responsePayload) => {
  if (!responsePayload || typeof responsePayload !== 'object') {
    return null;
  }

  return extractBenefitAmount(responsePayload.data ?? responsePayload.Data ?? responsePayload);
};


// Function to mask email
export const maskEmail = (email) => {
    const [localPart, domain] = email.split('@');
    const maskedLocalPart = localPart.length > 2 ? localPart[0] + '****' + localPart[localPart.length - 1] : localPart;
    return `${maskedLocalPart}@${domain}`;
};

export const maskPhoneNumber = (phoneNumber) => {
    if (phoneNumber && phoneNumber.startsWith('+233')) {
        // Replace the +233 with 0 and mask the beginning, leaving the last 4 digits visible
        const maskedPhone = '0' + '****' + phoneNumber.slice(-4);
        return maskedPhone;
    }
    return phoneNumber;
};


export const handleOnboardClick = async (product, history, userType) => {
  // Use product.name if available; otherwise, fallback to product itself
  const productName = product?.name || product || 'Unknown Product';
  const productInitials = getProductInitials(productName);
  const user = store.getState().user ?? {};
  let policyEligibilitySummary = null;

  if ((userType || '').toLowerCase() === 'client') {
    if (!user.phone_number) {
      toast.error('Unable to verify policy eligibility because the client phone number is missing.');
      return { allowed: false };
    }

    try {
      const eligibility = await fetchPolicyPurchaseEligibility(user.phone_number, productInitials);
      policyEligibilitySummary = eligibility?.policy_limit_summary || null;
      const blockMessage = getPolicyPurchaseBlockMessage(eligibility, productInitials);

      if (blockMessage) {
        toast.error(blockMessage);
        return { allowed: false, eligibility };
      }
    } catch (error) {
      toast.error(error.message || 'Unable to verify policy eligibility right now.');
      return { allowed: false, error };
    }
  }

  const applicationID = generateApplicationID(productName);

  const stepsCount = determineStepsCount(applicationID.substring(0, 3));
  let formSubmissionStatus = {};
  for (let i = 0; i < stepsCount; i++) {
    formSubmissionStatus[i] = false;
  }

  localStorage.setItem('currentApplicationID', applicationID);

  const ongoingApplications = JSON.parse(localStorage.getItem('ongoingApplications') || '[]');

  const newApplication = {
    id: applicationID,
    product: productName,
    data: {
      policyEligibilitySummary,
      clientData: {
        firstName: "",
        otherNames: "",
        lastName: "",
        dob: null,
        gender: "",
        occupation: "",
        idType: "",
        ghanaCardNumber: "",
        ghanaCardFrontFile: null,
        ghanaCardBackFile: null,
        email: "",
        clientPostalAddress: "",
        residentialAddress: "",
        maritalStatus: "",
        title: "",
        mobileNumber: ""
      }
    },
    status: 'Uncompleted',
    formSubmissionStatus,
  };

  const updatedOngoingApplications = [...ongoingApplications, newApplication];

  localStorage.setItem('ongoingApplications', JSON.stringify(updatedOngoingApplications));

  history.push(`/app/${userType}/onboarding/${applicationID.substring(0, 3)}`);
};

  export const formatCurrency = (val) => {
    const amount = Number(val) || 0;
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
  };


export const predictGender = async (firstName, phoneNumber) => {
  try {
    const formattedPhoneNumber = phoneNumber.startsWith('+233') 
      ? formatPhoneNumber(phoneNumber).replace(/\s/g, '') 
      : phoneNumber;

    const payload = {
      First_Name: firstName,
      Phone_Number: formattedPhoneNumber,
      Api_User: apiUser,
      Api_Key: apiKey,
    };

    const response = await axios.post(`${apiUrl}/SL_PredictUpdateGender`, payload);

    if (response.data.message_code === 100) {
      return response.data.predicted_gender;
    } else {
      console.error('Error predicting gender:', response.data.message);
      return null; // Gracefully fail
    }
  } catch (error) {
    console.error('Error during gender prediction:', error);
    return null; // Gracefully fail
  }
};


export const generatePDFBlob = async () => {
  const pdfInstance = <ProposalPDF />;
  return await pdf(pdfInstance).toBlob();
};

export const downloadPDF = async () => {
  try {
    const blob = await generatePDFBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Proposal_Form.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};




export const uploadPDF = async () => {
  try {
    const blob = await generatePDFBlob();
    const base64String = await blobToBase64(blob);
    const base64Content = base64String.split(',')[1]; // Remove data URL prefix

    const payload = {
      Document: base64Content,
      Document_Type: "PR",
      Proposal_No: localStorage.getItem('proposal_number').toString(),
      Api_User: apiUser,
      Api_Key: apiKey,
    };

    const response = await axios.post(`${apiUrl}/SL_AttachDocument`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });

    return {
      success: true,
      data: response.data,
      proposalNumber: payload.Proposal_No
    };
  } catch (error) {
    console.error('Upload failed:', error);
    throw error;
  }
};

// Helper function to convert blob to base64
const blobToBase64 = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};



export const getCoverTypeCodes = (initials, formValues, dependents = []) => {
  const baseCodes = [];
  const additionalCodes = [];

  // Handle product-specific base cover types
  switch(initials) {
    case 'WPP':
    case 'PWP':
    case 'FWP':
      baseCodes.push(2017459, 2018572);
      break;
      
    case 'UPP':
    case 'FUP':
    case 'PUP':
      baseCodes.push(2017475); // Base cover
      if (formValues.dreadDiseaseUPP === 'YES') {
        additionalCodes.push(2018583); 
      }
      break;
      
    case 'CBP':
      if (formValues.dreadDiseaseCBP === 'YES') {
        additionalCodes.push(2017457);
      }
      break;
      
    case 'CLP':
    case 'FCL':
    case 'PCL':
      if (formValues.disability === 'YES') {
        additionalCodes.push(2017452);
      }
      if (formValues.retrenchment === 'YES') {
        additionalCodes.push(2017454);
      }
      if (formValues.dreadDisease === 'YES') {
        additionalCodes.push(2017453);
      }
      if (formValues.personalAccident === 'YES') {
        additionalCodes.push(2017455);
      }
      break;
      
    case 'EHP':
    case 'PEH':
    case 'FEH':
      baseCodes.push(2017475); // Main cover
      dependents.forEach(dep => {
        const relationship = dep.relationship.toUpperCase();
        const code = DEPENDENT_CVT_CODES.EHP[relationship] || 
                    DEPENDENT_CVT_CODES.EHP['EXTENDED-FAMILY'];
        additionalCodes.push(code);
      });
      break;
      
    case 'FPP':
      // Main cover is implied (no specific code needed)
      dependents.forEach(dep => {
        const relationship = dep.relationship.toUpperCase();
        if (DEPENDENT_CVT_CODES.FPP[relationship]) {
          additionalCodes.push(DEPENDENT_CVT_CODES.FPP[relationship]);
        }
      });
      if (formValues.retrenchment === 'YES') {
        additionalCodes.push(DEPENDENT_CVT_CODES.FPP.RETRENCHMENT);
      }
      break;
      
    default:
      // For unknown products, return empty array (or handle differently)
      return [];
  }

  return [...new Set([...baseCodes, ...additionalCodes])]; // Remove duplicates
};

export const fetchProductData = async (initials, selectedPlanOption = null) => {
  try {
    // 1. Find the product by initials (e.g., "EHP")
    const findProductsResponse = await axios.post(`${apiUrl}/SL_FindProducts`, {
      Api_User: apiUser,
      Api_Key: apiKey,
    });

    const matchedProduct = findProductsResponse.data.find(
      (product) => product.prodShtDesc === initials
    );

    if (!matchedProduct) {
      throw new Error(`No product found for initials: ${initials}`);
    }

    const productCode = matchedProduct.prodCode;

    // 2. Get all available product options
    const optionsResponse = await axios.post(`${apiUrl}/SL_ProductOptions`, {
      Product_Code: productCode.toString(),
      Api_User: apiUser,
      Api_Key: apiKey,
    });

    let productOptionCode;

    // For EHP/FPP, we need to match the selected plan (Basic, Standard, etc.)
    if (['EHP', 'PEH', 'FEH', 'FPP'].includes(initials) && selectedPlanOption) {
      const selectedOption = optionsResponse.data.find(
        (opt) => opt.popDesc === selectedPlanOption
      );
      productOptionCode = selectedOption?.popCode;
    } else {
      // For simple products (WPP, CLP, etc.), just take the first option
      productOptionCode = optionsResponse.data[0]?.popCode;
    }

    // 3. Get cover types for the selected option
    let coverTypes = [];
    if (productOptionCode) {
      const coverTypesResponse = await axios.post(`${apiUrl}/SL_CoverTypes`, {
        Product_Code: productCode.toString(),
        Product_Option_Code: productOptionCode.toString(),
        Api_User: apiUser,
        Api_Key: apiKey,
      });
      coverTypes = coverTypesResponse.data;
    }

    console.log('product code from fetchProductData:', productCode);
    console.log('product options from fetchProductData:', optionsResponse.data);
    console.log('cover types from fetchProductData:', coverTypes);

    return {
      productCode,
      productOptions: optionsResponse.data,
      coverTypes,
    };
  } catch (error) {
    console.error("Error fetching product data:", error);
    throw error;
  }
};



/**
 * Calculate age from date of birth
 * @param {string|Date} dob - Date of birth (can be Date object, string in various formats including ISO)
 * @returns {number} - Age in years, or null if invalid
 */
export const calculateAge = (dob) => {
  if (!dob) return null;
  
  let birthDate;
  
  // Handle different input formats
  if (dob instanceof Date) {
    birthDate = dob;
  } else if (typeof dob === 'string') {
    // Try to parse ISO format (YYYY-MM-DDTHH:MM:SS.sssZ)
    // ISO format is automatically handled by new Date()
    birthDate = new Date(dob);
    
    // If new Date() failed to parse correctly, try manual parsing for DD-MM-YYYY
    if (isNaN(birthDate.getTime())) {
      // Handle DD-MM-YYYY format
      if (dob.includes('-')) {
        const parts = dob.split('-');
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
          // DD-MM-YYYY format
          birthDate = new Date(parts[2], parts[1] - 1, parts[0]);
        } else if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
          // YYYY-MM-DD format (already handled by new Date, but just in case)
          birthDate = new Date(parts[0], parts[1] - 1, parts[2]);
        }
      }
      // Handle DD/MM/YYYY format
      else if (dob.includes('/')) {
        const parts = dob.split('/');
        if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
          birthDate = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      }
    }
  } else {
    return null;
  }
  
  // Check if date is valid
  if (isNaN(birthDate.getTime())) {
    console.warn('Invalid date provided to calculateAge:', dob);
    return null;
  }
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// Alias for dependents calculation (same function)
export const dependentsAgeCalculation = (dob) => {
  return calculateAge(dob);
};